import "./ClassicyColorPicker.scss";
import {
	type CSSProperties,
	type FC,
	useContext,
	useEffect,
	useState,
} from "react";
import { intToHex } from "@/SystemFolder/ControlPanels/AppearanceManager/ClassicyColors";
import { ClassicyAppIdContext } from "@/SystemFolder/SystemResources/App/ClassicyAppIdContext";
import { ClassicyButton } from "@/SystemFolder/SystemResources/Button/ClassicyButton";
import { ClassicyWindow } from "@/SystemFolder/SystemResources/Window/ClassicyWindow";
import { ClassicyColorPickerCMYK } from "./ClassicyColorPickerCMYK";
import { ClassicyColorPickerCrayon } from "./ClassicyColorPickerCrayon";
import {
	type ClassicyCrayon,
	MAC_OS_8_CRAYONS,
} from "./ClassicyColorPickerCrayons";
import { ClassicyColorPickerHLS } from "./ClassicyColorPickerHLS";
import { ClassicyColorPickerHSV } from "./ClassicyColorPickerHSV";
import { ClassicyColorPickerRGB } from "./ClassicyColorPickerRGB";

type PickerMode = "Crayons" | "RGB" | "HSV" | "HLS" | "CMYK";
const PICKER_MODES: PickerMode[] = ["Crayons", "RGB", "HSV", "HLS", "CMYK"];

interface ClassicyColorPickerDialogProps {
	id: string;
	/** The app this dialog's window registers under. Precedence: this prop,
	 *  then the lexically enclosing {@link ClassicyAppIdContext}, then
	 *  unregistered (the window opens without an app record — today's
	 *  behavior when neither is available). Deliberately NOT derived from
	 *  the store's global focused app: that would register the dialog under
	 *  whichever app happens to be focused, which can be the wrong app (the
	 *  owner is open but unfocused) or can migrate mid-session (a focus
	 *  change elsewhere while the dialog stays open), stealing focus either
	 *  way. */
	appId?: string;
	open: boolean;
	initialColor?: number;
	crayons?: ClassicyCrayon[];
	onSelectFunc?: (color: number) => void;
	onCancelFunc?: () => void;
}

export const ClassicyColorPickerDialog: FC<ClassicyColorPickerDialogProps> = ({
	id,
	appId,
	open,
	initialColor = 0x000000,
	crayons = MAC_OS_8_CRAYONS,
	onSelectFunc,
	onCancelFunc,
}) => {
	const [pendingColor, setPendingColor] = useState(initialColor);
	const [mode, setMode] = useState<PickerMode>("Crayons");
	// Lexical, not global: see the `appId` doc comment above for why this
	// must not fall back to the store's focused app.
	const contextAppId = useContext(ClassicyAppIdContext);
	const resolvedAppId = appId ?? contextAppId;

	// Reset to initialColor whenever the dialog is freshly opened.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (open) setPendingColor(initialColor);
	}, [open]);

	if (!open) return null;

	const handleOK = () => {
		onSelectFunc?.(pendingColor);
	};

	const activePanel = (() => {
		switch (mode) {
			case "Crayons":
				return (
					<ClassicyColorPickerCrayon
						color={pendingColor}
						crayons={crayons}
						onChangeFunc={setPendingColor}
					/>
				);
			case "RGB":
				return (
					<ClassicyColorPickerRGB
						color={pendingColor}
						onChangeFunc={setPendingColor}
					/>
				);
			case "HSV":
				return (
					<ClassicyColorPickerHSV
						color={pendingColor}
						onChangeFunc={setPendingColor}
					/>
				);
			case "HLS":
				return (
					<ClassicyColorPickerHLS
						color={pendingColor}
						onChangeFunc={setPendingColor}
					/>
				);
			case "CMYK":
				return (
					<ClassicyColorPickerCMYK
						color={pendingColor}
						onChangeFunc={setPendingColor}
					/>
				);
		}
	})();

	return (
		<ClassicyWindow
			id={id}
			appId={resolvedAppId ?? id}
			title="Color Picker"
			modal={true}
			closable={true}
			zoomable={false}
			collapsable={false}
			resizable={false}
			scrollable={false}
			initialSize={[520, 430]}
			initialPosition={[200, 100]}
			onCloseFunc={() => onCancelFunc?.()}
			analyticsExclude
		>
			<div className="classicyColorPickerDialogBody">
				<ul
					className="classicyColorPickerModeList"
					role="listbox"
					aria-label="Color picker mode"
				>
					{PICKER_MODES.map((m) => (
						<li
							key={m}
							role="option"
							aria-selected={m === mode}
							className={
								m === mode
									? "classicyColorPickerModeListItemSelected"
									: undefined
							}
							onClick={() => setMode(m)}
						>
							{m}
						</li>
					))}
				</ul>
				<div className="classicyColorPickerActivePanel">{activePanel}</div>
			</div>
			<div
				className="classicyColorPickerPreview"
				style={
					{
						"--classicy-preview-color": intToHex(pendingColor),
					} as CSSProperties
				}
				aria-label={`Selected color: ${intToHex(pendingColor)}`}
			/>
			<div className="classicyColorPickerDialogFooter">
				<ClassicyButton onClickFunc={onCancelFunc}>Cancel</ClassicyButton>
				<ClassicyButton isDefault={true} onClickFunc={handleOK}>
					OK
				</ClassicyButton>
			</div>
		</ClassicyWindow>
	);
};
